// Story 024 — focus-equals-review effect.
//
// Subscribes to `selectionStore.activeFindingId` (which doubles as the
// active defect id when a defect row / tile / Sketch marker / Viewer
// BBox is clicked) and, when:
//
//   * the active id maps to a defect in the cache,
//   * the inspection is in `PRE_SCREENING` or `EXPERT_ANALYSIS`,
//   * the D-5 edit gate is open,
//   * the user has interacted with the page at least once
//     (page-load default activation does NOT fire — Q2 in story 024),
//   * the defect's current status matches the phase's prereq
//     (`detected` for PRE_SCREENING, `reviewed` for EXPERT_ANALYSIS),
//
// fires `useTransitionDefectStatusMutation`. The transition is
// debounced per `(defectId, targetStatus)` within a 1-second window —
// rapid arrow-key navigation across the SAME row will not fire two
// PATCHes. Cross-defect rapid presses each fire their own.
//
// Errors surface as a Snackbar via `setStatusSnack` (parented in
// AnalysisFeature.tsx and rendered alongside the DefectsPanel one).
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Defect, GetDefectsResponse } from "@aeriosiq/types";
import { defectKeys } from "../defects/queries.js";
import {
  useTransitionDefectStatusMutation,
  type TransitionDefectStatusVars,
} from "../defects/queries.js";
import { planFocusTransition } from "../defects/statusExt.js";
import { useImageActivationStore } from "./state/imageActivationStore.js";
import { useSelectionStore } from "./state/selectionStore.js";

/** 1-second per-defect debounce per AC10. */
const DEBOUNCE_WINDOW_MS = 1000;

export interface UseFocusEqualsReviewArgs {
  inspectionId: string;
  evaluationState: string | null;
  editAllowed: boolean;
  onError?: () => void;
}

/**
 * Install the focus-equals-review side effect. Must mount once inside
 * `AnalysisLayoutMount`. Returns nothing — purely an effect.
 *
 * The "user has interacted at least once" gate is implemented by
 * binding a single-shot `pointerdown` / `keydown` listener at the
 * document level on mount. Until that flips, every activation is
 * treated as a page-load no-op.
 */
export function useFocusEqualsReviewEffect(
  args: UseFocusEqualsReviewArgs,
): void {
  const { inspectionId, evaluationState, editAllowed, onError } = args;
  const qc = useQueryClient();
  const transition = useTransitionDefectStatusMutation(inspectionId);
  // Track the (defectId, targetStatus) pairs we've fired recently with
  // their wall-clock timestamps. Cleared lazily on each fire.
  const recentRef = useRef<Map<string, number>>(new Map());
  // "User has interacted at least once" gate. Flipped by the document
  // listener.
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    const flip = () => {
      hasInteractedRef.current = true;
    };
    document.addEventListener("pointerdown", flip, {
      capture: true,
      once: true,
    });
    document.addEventListener("keydown", flip, {
      capture: true,
      once: true,
    });
    return () => {
      document.removeEventListener("pointerdown", flip, { capture: true });
      document.removeEventListener("keydown", flip, { capture: true });
    };
  }, []);

  useEffect(() => {
    // Subscribe imperatively — `useEffect` on a zustand selector value
    // would re-run on every activation but also tear down on every
    // re-render; we want the listener stable across renders.
    return useSelectionStore.subscribe((state, prev) => {
      const next = state.activeFindingId;
      if (next == null) return;
      if (next === prev.activeFindingId) return;
      if (!hasInteractedRef.current) return;
      if (!editAllowed) return;
      // Look up the defect in any cached defects list. The active id
      // may also be a finding id (story 005 cascade); skip silently if
      // we can't find a defect.
      const lists = qc.getQueriesData<GetDefectsResponse>({
        queryKey: [...defectKeys.all, "list", inspectionId],
      });
      let defect: Defect | null = null;
      for (const [, data] of lists) {
        if (!data) continue;
        const found = data.items.find((d) => d.id === next);
        if (found) {
          defect = found;
          break;
        }
      }
      if (!defect) return;
      const plan = planFocusTransition({
        evaluationState,
        defectStatus: defect.status,
      });
      if (!plan) return;
      // Debounce per (defectId, targetStatus).
      const key = `${defect.id}::${plan.targetStatus}`;
      const last = recentRef.current.get(key) ?? 0;
      const now = Date.now();
      if (now - last < DEBOUNCE_WINDOW_MS) return;
      recentRef.current.set(key, now);
      const imageId = useImageActivationStore.getState().activeImageId ?? "";
      const vars: TransitionDefectStatusVars = {
        defectId: defect.id,
        fromStatus: defect.status,
        targetStatus: plan.targetStatus,
        reviewPhase: plan.reviewPhase,
        dataAcquisitionId: inspectionId,
        imageId,
      };
      transition.mutate(vars, {
        onError: () => {
          onError?.();
        },
      });
    });
  }, [inspectionId, evaluationState, editAllowed, qc, transition, onError]);
}
